import { createApi, retry } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery, getApiBaseUrl } from 'modules/api/utils';
import { UrlNames } from 'modules/app/constants';
import { configService } from 'modules/app/services';

import { LiveKitMediaTestSession, TechCheckResultRequest } from './types';

// natalie-api's accountless service. Never retried: every attempt spends the endpoint's throttle,
// and a 429 is an answer, not a fault.
const accountlessNatalieBaseQuery = axiosBaseQuery({
  baseURL: () => configService.getUrl(UrlNames.AccountlessNatalieApi),
});

export const techCheckApi = createApi({
  reducerPath: 'techCheckApi',
  baseQuery: retry(axiosBaseQuery({ baseURL: getApiBaseUrl('v1') }), { maxRetries: 3 }),
  endpoints: (builder) => ({
    postTechCheckResult: builder.mutation<void, TechCheckResultRequest>({
      query: (data) => ({
        url: `/techcheck`,
        method: 'POST',
        data,
      }),
    }),
    // A throwaway LiveKit room for the Natalie media check: no Natalie, no conversation, no record.
    createLiveKitMediaTestSession: builder.mutation<LiveKitMediaTestSession, void>({
      queryFn: async (_arg, api, extraOptions) => {
        const result = await accountlessNatalieBaseQuery(
          { url: '/v2/natalie/media-test/session', method: 'POST' },
          api,
          extraOptions
        );
        if (result.error) {
          // The base query reports "no response at all" (blocked, offline) as a 500; the probe
          // needs to tell that apart from the service answering with an error.
          const hasResponse = result.meta?.response !== undefined;
          return {
            error: hasResponse ? result.error : { status: 'FETCH_ERROR', error: String(result.error.data) },
          };
        }
        return { data: result.data as LiveKitMediaTestSession };
      },
    }),
  }),
});

export const { usePostTechCheckResultMutation, useCreateLiveKitMediaTestSessionMutation } = techCheckApi;
